import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { MdDelete } from "react-icons/md";
import { deleteUser as deleteReduxUser, deleteOrg, settingFalse } from '../../../redux/actions';
import API from '../../../utils/API';
import './SettingsModal.css';

interface SettingsModalProps {
  show: any;
  onSave: (data: any) => void;
}

const SettingsModal: React.FC<SettingsModalProps> = ({ show, onSave }) => {
  const reduxUser = useSelector((state: any) => state.signin);
  const dispatch = useDispatch();
  const history = useHistory();

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    phoneNumber: '',
    currentPassword: '',
    confirmPassword: '',
    newPassword: ''
  });

  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [isLoading, setIsLoading] = useState(false);
  // const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [message, setMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Initialize form data when modal opens or user data changes
  useEffect(() => {
    if (reduxUser && show) {
      setFormData({
        email: reduxUser.email || '',
        phoneNumber: reduxUser.telephone || '',
        currentPassword: '',
        confirmPassword: '',
        newPassword: ''
      });
      setErrors({});
      setMessage(null);
    }
  }, [reduxUser, show]);

  // Auto-close message after 2 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage(null);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Phone validation (optional field)
    if (formData.phoneNumber && !/^\+?[\d\s\-\(\)]+$/.test(formData.phoneNumber)) {
      newErrors.phoneNumber = 'Please enter a valid phone number';
    }

    // Password validation (only if user is changing password)
    if (formData.confirmPassword || formData.newPassword) {
      if (!formData.currentPassword) {
        newErrors.currentPassword = 'Current password is required to change password';
      }
      if (!formData.newPassword) {
        newErrors.newPassword = 'New password is required';
      } else if (formData.newPassword.length < 8) {
        newErrors.newPassword = 'Password must be at least 8 characters long';
      }
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your new password';
      } else if (formData.newPassword !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      // Update general info (email, phone) if changed
      if (formData.email !== reduxUser.email || formData.phoneNumber !== reduxUser.telephone) {
        const updatedUserData = {
          _id: reduxUser._id,
          email: formData.email,
          telephone: formData.phoneNumber
        };
        await API.updateUser(updatedUserData);
      }

      // Update password if provided
      if (formData.currentPassword && formData.newPassword) {
        // First verify current password
        const passwordCheck = await API.checkPassword(reduxUser._id, formData.currentPassword);
        if (!passwordCheck.data.passwordMatch) {
          setErrors(prev => ({
            ...prev,
            currentPassword: 'Current password is incorrect'
          }));
          return;
        }

        // Update password
        await API.updatePassword(reduxUser._id, formData.newPassword);
      }

      // Call the parent's onSave callback
      await onSave(formData);

      // Show success message (modal will auto-close after 2 seconds)
      setMessage({ type: 'success', text: 'Settings saved successfully!' });
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage({ type: 'error', text: 'Error saving settings. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAccount = () => {
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    setShowDeleteConfirm(false);
    try {
      // Call the delete user API
      await API.deleteUser(reduxUser._id);

      // Clear Redux state
      dispatch(deleteReduxUser());
      dispatch(deleteOrg());

      // Redirect to home page
      history.push('/');

      setMessage({ type: 'success', text: 'Account deleted successfully.' });
    } catch (error) {
      console.error('Error deleting account:', error);
      setMessage({ type: 'error', text: 'Error deleting account. Please try again.' });
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  if (!show) return null;

  return (
    <div className="settings-modal-overlay" onClick={() =>{dispatch(settingFalse(false))}}>
      <div className="settings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="settings-modal-header">
          <h2>Settings</h2>
          <button className="settings-modal-close" onClick={() =>{dispatch(settingFalse(false))}}>
            ✕
          </button>
        </div>

        <div className="settings-modal-body">
          {/* General Info Section */}
          <div className="settings-section">
            <h3>General info</h3>

            <div className="settings-form-group">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={errors.email ? 'error' : ''}
                placeholder="Enter your email"
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>

            <div className="settings-form-group">
              <label htmlFor="phoneNumber">Phone number</label>
              <input
                id="phoneNumber"
                type="tel"
                value={formData.phoneNumber}
                onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                className={errors.phoneNumber ? 'error' : ''}
                placeholder="Enter your phone number"
              />
              {errors.phoneNumber && <span className="error-message">{errors.phoneNumber}</span>}
            </div>
          </div>

          {/* Security Section */}
          <div className="settings-section">
            <h3>Security Section</h3>

            <div className="settings-form-group">
              <label htmlFor="currentPassword">Password</label>
              <div className="password-input-container">
                <input
                  id="currentPassword"
                  type={'password'}
                  value={formData.currentPassword}
                  onChange={(e) => handleInputChange('currentPassword', e.target.value)}
                  className={errors.currentPassword ? 'error' : ''}
                  placeholder="Enter current password"
                />
                {/* <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? <FaEye /> : <FaEyeSlash />}
                </button> */}
              </div>
              {errors.currentPassword && <span className="error-message">{errors.currentPassword}</span>}
            </div>
            <div className="settings-form-group">
              <label htmlFor="newPassword">New password</label>
              <div className="password-input-container">
                <input
                  id="newPassword"
                  type={showNewPassword ? 'text' : 'password'}
                  value={formData.newPassword}
                  onChange={(e) => handleInputChange('newPassword', e.target.value)}
                  className={errors.newPassword ? 'error' : ''}
                  placeholder="Enter a strong password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <FaEye /> : <FaEyeSlash />}
                </button>
              </div>
              {errors.newPassword && <span className="error-message">{errors.newPassword}</span>}
            </div>
            <div className="settings-form-group">
              <label htmlFor="confirmPassword">Confirm password</label>
              <div className="password-input-container">
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                  className={errors.confirmPassword ? 'error' : ''}
                  placeholder="Confirm password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <FaEye /> : <FaEyeSlash />}
                </button>
              </div>
              {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
            </div>
          </div>
        </div>

        {/* Message Modal */}
        {message && (
          <div className={`message-modal ${message.type}`}>
            <div className="message-content">
              <span>{message.text}</span>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="delete-confirm-overlay" onClick={handleCancelDelete}>
            <div className="delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
              <div className="delete-confirm-header">
                <h3>Delete Account</h3>
                <button className="delete-confirm-close" onClick={handleCancelDelete}>
                  ✕
                </button>
              </div>
              <div className="delete-confirm-body">
                <p>Are you sure you want to delete your account? This action cannot be undone and will permanently delete all your data.</p>
              </div>
              <div className="delete-confirm-footer">
                <button className="delete-confirm-btn delete-confirm-cancel" onClick={handleCancelDelete}>
                  Cancel
                </button>
                <button className="delete-confirm-btn delete-confirm-delete" onClick={handleConfirmDelete}>
                  Delete Account
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="settings-modal-footer">
          <button
            className="settings-btn settings-btn-primary"
            onClick={handleSave}
            disabled={isLoading}
          >
            {isLoading ? 'Saving...' : 'Save Changes'}
          </button>
          <button
            className="settings-btn settings-btn-secondary"
            onClick={() =>{dispatch(settingFalse(false))}}
            disabled={isLoading}
          >
            Close
          </button>
        </div>

        <div className="settings-modal-delete">
          <button className="delete-account-btn" onClick={handleDeleteAccount}>
          <MdDelete style={{fontSize:"20px"}}/> Delete account
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;